/**
 * Submission Data Parser Utility
 *
 * Handles the flexible format: { fields: SubmissionField[] }
 */

export interface SubmissionField {
  label: string
  value: string
  type?: 'text' | 'textarea' | 'select' | 'video' | 'image' | 'file'
  order: number
}

export interface ParsedSubmissionData {
  fields: SubmissionField[]
  rawData?: any // Original data for debugging
}

/**
 * Parse submission data from flexible format or JSON string
 */
export function parseSubmissionData(
  data: string | object | null | undefined,
): ParsedSubmissionData {
  if (!data) {
    return { fields: [] }
  }

  let parsed: any = data

  // If it's a string, try to parse as JSON
  if (typeof data === 'string') {
    try {
      parsed = JSON.parse(data)
    } catch (e) {
      // Not JSON, treat as plain text
      return {
        fields: [
          {
            label: 'Description',
            value: data,
            type: 'textarea',
            order: 1,
          },
        ],
        rawData: data,
      }
    }
  }

  // Flexible format: has fields array
  if (parsed.fields && Array.isArray(parsed.fields)) {
    return {
      fields: (parsed.fields as SubmissionField[]).sort((a, b) => a.order - b.order),
      rawData: parsed,
    }
  }

  // Fallback for plain text or unexpected format
  return {
    fields: [
      {
        label: 'Content',
        value: String(parsed),
        type: 'textarea',
        order: 1,
      },
    ],
    rawData: parsed,
  }
}

/**
 * Create submission data in the new format
 */
export function createSubmissionData(
  fields: Array<{ label: string; value: string; type?: string }>,
): object {
  return {
    fields: fields.map((field, index) => ({
      label: field.label,
      value: field.value,
      type: field.type || 'text',
      order: index + 1,
    })),
  }
}

/**
 * Get a specific field value by label
 */
export function getFieldValue(data: ParsedSubmissionData, label: string): string | null {
  const field = data.fields.find((f) => f.label.toLowerCase() === label.toLowerCase())
  return field?.value || null
}
